import { useAuthMe } from '@h2hspark/auth-ui';
import { Skeleton } from '@h2hspark/ui';
import { CalendarCheck } from 'lucide-react';
import * as React from 'react';

import { useMyRegistration } from './api';
import { CONNECT_CLOSED_AFTER_EVENT, EVENT_ENDED_HEADING, confirmedLine } from './constants';

/**
 * **What the event page shows a registrant once the evening is over** (`MEM-015` AC-7).
 *
 * ## The gap this closes
 *
 * `MEM-015` AC-1 calls this page *"the page they return to once registered"*, and nothing in that
 * story said what it shows once the event has happened. Nothing in the code did either:
 * `openForRegistration` is `lifecycleState === 'published'` and says nothing about the clock, so a
 * published event that finished last week kept the whole live page — a pinned **Reserve my spot**
 * bar, an **Add to calendar** control for an evening that is over, a **Cancel registration**
 * control for a place that has been used, and a **Connect** pill leading to a surface the server
 * closed at `endsAt`.
 *
 * So this is not "the confirmed panel with some things hidden". It is the panel in the past tense,
 * and the page drops the reserve bar beside it.
 *
 * ## Since 2026-09-11 it is two sentences and no controls
 *
 * Sumit, on this panel while reviewing #528: *"I guess it can be disabled but you still want to
 * leave it as followup is still applicable. keep going."* The header card above it carries the
 * ticket, the matches and the follow-up now — its *View your ticket* control and the stepper's
 * *Before · Matches* and *After · Follow up now* — so the three buttons here were the same three
 * things a second time. What is left is what only this panel says: that the evening is over, under
 * which order, and what happened to Connect.
 *
 * ## What the page keeps, and the one that was ruled
 *
 *   - **Ticket.** Sumit, 2026-09-03, in as many words: *"make sure View ticket is still reachable
 *     from the event page."* It is the record of the door a member went through — `MEM-022` AC-1's
 *     *"Admits 1 · order #{order number}"* — and it is the reason `hasEnded` is a field of its own
 *     rather than a narrowing of `openForRegistration`, which would have taken the ticket off the
 *     page along with the Reserve bar. The header card's *View your ticket* opens it.
 *   - **Matches.** A match set is a **snapshot** (`MEM-026`), so it survives the evening and is
 *     worth reading after it — the stepper's *Before* part opens a released one, and says so when
 *     none was released.
 *   - **Follow up.** What the evening produced (`MEM-035`, `MEM-036`), and the half of this page a
 *     member comes back for days later rather than on the night. It is the stepper's *After* part,
 *     drawn in the prototype's call-to-action variant — *Follow up now* — because on a page about
 *     an evening that is over it is the only thing still live.
 *
 * ## What it drops, and why each is a drop rather than a disabled control
 *
 *   - **Connect**, with a sentence in its place. `MEM-031` AC-7 closes the connect code, the
 *     scanner, the open-to-connect toggle and the in-event roster at event end and invalidates the
 *     token; the server enforces every word (`connectWindowState`), and `MEM-031` AC-1's own
 *     instruction is that a control which cannot work is worse than its absence. So
 *     {@link CONNECT_CLOSED_AFTER_EVENT} says what closed and where the people went, and there is
 *     no pill.
 *   - **Add to calendar.** An `.ics` for last Thursday.
 *   - **Cancel registration.** There is no seat left to release and no refund window left open
 *     (`MEM-023`); the API refuses it, and a control whose only outcome is a served refusal is a
 *     control that exists to disappoint.
 *   - **The install offer.** `PLT-008` AC-5's moment is *"after the member's first completed
 *     registration"* — the minute they have just seen the product work — not a page they opened a
 *     fortnight later.
 *
 * ## No figure is drawn here, deliberately
 *
 * The obvious additions are a count of people met and a count of follow-ups outstanding, and both
 * would be figures this component cannot compute: neither is on the public event payload, and a
 * `0` rendered while the real number is unknown is the defect `EVT-039` AC-4 names and that this
 * repository has shipped three times in a week. The two screens that *can* compute them are one
 * tap away and say so themselves.
 *
 * ## Who sees it
 *
 * A registrant, and nobody else. A signed-out reader and a signed-in member who never registered
 * both get `null` — there is nothing to offer them about an evening that has happened, and the
 * sign-in prompt `RegistrationPanel` draws is an invitation to register, which is not available
 * either. The description, the host, the tags and the location all still render above: `MEM-015`
 * AC-4 is *"in every state, for every reader"*, and this component is not in that path.
 *
 * A member whose registration was **cancelled** also gets `null`, because `useMyRegistration`
 * answers `null` for one (`MEM-017` AC-6, server-side) — the same answer they get before the
 * event, and the same one they get on the live page.
 */
export function PastEventPanel({ eventId }: { readonly eventId: string }): React.ReactElement | null {
  const { user, loading } = useAuthMe();
  const signedIn = user !== null;
  const query = useMyRegistration(eventId, { enabled: signedIn });

  if (loading && user === null) return <Skeleton className="h-24 w-full rounded-lg" />;
  if (!signedIn) return null;
  if (query.isPending) return <Skeleton className="h-24 w-full rounded-lg" />;

  const registration = query.data?.registration ?? null;
  if (registration === null) return null;

  return (
    <section
      data-testid="past-event-panel"
      className="space-y-3 rounded-lg border border-border-subtle bg-muted/40 p-4"
    >
      <div className="flex items-center gap-2.5">
        <span
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-muted text-muted-foreground"
          aria-hidden
        >
          <CalendarCheck className="h-5 w-5" />
        </span>
        <div>
          <p className="font-brand text-md font-extrabold text-foreground">
            {EVENT_ENDED_HEADING}
          </p>
          {/* The same line the confirmed panel drew, and the same confirmation code: one number
              across the ticket, the panel and the member's own history. */}
          <p className="text-xs text-muted-foreground">
            {confirmedLine(registration.confirmationCode)}
          </p>
        </div>
      </div>

      {/* `MEM-031` AC-7: what replaced the connect code and the scanner, said out loud rather than
          drawn as a pill that leads to a closed screen. */}
      <p className="text-sm leading-relaxed text-text-body">{CONNECT_CLOSED_AFTER_EVENT}</p>
    </section>
  );
}
